/**
 * FormulaSetActions — the Formula tab's library controls: save the current
 * groups as a set (fork-aware) and load one back (replace-confirmed, fresh
 * ids, rebalanced weights).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useState } from 'react';
import type { FlowOptimizerFormulaSet, FormulaConfig } from '@trafficpulse/shared';

import { ConfirmDialog } from '@/components/ui/layout/ConfirmDialog';
import { replaceFormulaFromPayload } from '@/features/flow-optimizer/clipboard/formula-clipboard.apply';

import { buildFormulaSetPayload } from '../envelope/serialize-library';
import {
  EditForkDialog,
  SaveSetButton,
  SaveSetDialog,
  useSaveSet,
} from '../save';
import SetPickerMenu from './SetPickerMenu';

export interface FormulaSetActionsProps {
  formula: FormulaConfig;
  onChange: (next: FormulaConfig) => void;
  /** Draft's loaded package — "Save as copy" repoints it. */
  packageId?: string | null;
}

const FormulaSetActions: FC<FormulaSetActionsProps> = ({
  formula,
  onChange,
  packageId,
}) => {
  const save = useSaveSet({
    kind: 'formula',
    buildPayload: () => ({ formula }),
    packageId,
  });
  const [pendingLoad, setPendingLoad] = useState<FlowOptimizerFormulaSet | null>(null);

  const applySet = (set: FlowOptimizerFormulaSet) => {
    onChange(replaceFormulaFromPayload(buildFormulaSetPayload(set.formula)));
    setPendingLoad(null);
  };

  return (
    <>
      <SaveSetButton
        disabledReason={
          formula.groups.length === 0
            ? 'Add at least one group to save a set.'
            : undefined
        }
        onClick={() => save.setDialogOpen(true)}
      />
      <SetPickerMenu
        kind="formula"
        onPick={(set) => {
          const formulaSet = set as FlowOptimizerFormulaSet;
          if (formula.groups.length > 0) setPendingLoad(formulaSet);
          else applySet(formulaSet);
        }}
      />
      <SaveSetDialog
        open={save.dialogOpen}
        kindLabel="formula"
        sets={save.sets}
        busy={save.saving}
        onSubmit={save.submit}
        onClose={() => save.setDialogOpen(false)}
      />
      <EditForkDialog
        open={save.forkPrompt !== null}
        setName={save.forkPrompt?.setName ?? ''}
        packages={save.forkPrompt?.packages ?? 0}
        campaigns={save.forkPrompt?.campaigns ?? 0}
        willRepoint={Boolean(packageId)}
        busy={save.saving}
        onUpdateAll={save.updateForEveryone}
        onSaveCopy={save.saveAsCopy}
        onCancel={save.cancelFork}
      />
      <ConfirmDialog
        open={pendingLoad !== null}
        title="Load set into the Formula tab?"
        description={
          pendingLoad
            ? `"${pendingLoad.name}" replaces the current formula groups. ` +
              'Nothing is saved until you press Save Config.'
            : ''
        }
        confirmLabel="Load set"
        onConfirm={() => pendingLoad && applySet(pendingLoad)}
        onCancel={() => setPendingLoad(null)}
      />
    </>
  );
};

export default FormulaSetActions;
